import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Mail, Printer, Search, Crown, Sparkles } from "lucide-react";

export function CertMailDialog({ open, onOpenChange, disputeId }) {
  const { user } = useAuth();
  const canUse = user?.plan === "basic" || user?.plan === "plus";

  const [addr, setAddr] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("aura_mail_addr") || "{}");
    } catch {
      return {};
    }
  });
  const [loading, setLoading] = useState(false);
  const [packet, setPacket] = useState(null);

  // Optional recipient override
  const [overrideOpen, setOverrideOpen] = useState(false);
  const [q, setQ] = useState("");
  const [matches, setMatches] = useState([]);
  const [overrideId, setOverrideId] = useState(null);

  useEffect(() => {
    if (!open) {
      setPacket(null);
      setQ("");
      setMatches([]);
      setOverrideId(null);
      setOverrideOpen(false);
    }
  }, [open]);

  useEffect(() => {
    if (q.length < 2) {
      setMatches([]);
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/creditors/search?q=${encodeURIComponent(q)}`);
        setMatches(data.matches || []);
      } catch {
        setMatches([]);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [q]);

  const render = async () => {
    if (!canUse) return;
    setLoading(true);
    try {
      const { data } = await api.post("/certmail/render", {
        dispute_id: disputeId,
        consumer_address_line1: addr.line1 || null,
        consumer_address_line2: addr.line2 || null,
        consumer_city: addr.city || null,
        consumer_state: addr.state || null,
        consumer_zip: addr.zip || null,
        override_creditor_id: overrideId,
      });
      setPacket(data);
      localStorage.setItem("aura_mail_addr", JSON.stringify(addr));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const print = () => {
    const el = document.getElementById("certmail-print-area");
    if (!el) return;
    const win = window.open("", "_blank", "width=800,height=1000");
    if (!win) {
      toast.error("Popup blocked — allow popups to print.");
      return;
    }
    // Build the print doc via DOM APIs (no document.write / no innerHTML injection).
    const doc = win.document;
    // Title (safe: only a static string)
    doc.title = "Cert-mail packet · Aura Credit";

    // Style block
    const style = doc.createElement("style");
    style.textContent = [
      "body{font-family:'IBM Plex Mono', ui-monospace, monospace; padding:48px; color:#111; line-height:1.5;}",
      "h1{font-family: Georgia, serif; font-style: italic; font-size: 22px; margin: 0 0 24px;}",
      ".row{display:flex; gap:64px; margin-bottom:48px;}",
      ".col{flex:1;}",
      ".label{font-size:10px; letter-spacing:0.2em; text-transform:uppercase; color:#666; margin-bottom:8px;}",
      "pre{white-space:pre-wrap; font: 12px/1.55 'IBM Plex Mono', ui-monospace, monospace; margin:0;}",
      "hr{border:none; border-top:1px solid #ddd; margin:32px 0;}",
      "ol{padding-left:20px;} li{margin-bottom:6px;}",
    ].join("");
    doc.head.appendChild(style);

    // Body — clone the print-area node; browsers set textContent for all text
    // nodes so nothing user-controlled can execute.
    const clone = el.cloneNode(true);
    doc.body.appendChild(clone);

    setTimeout(() => {
      win.focus();
      win.print();
    }, 250);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-[#131518] border-[#26282C] text-[#F5F3EE] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl">Cert-mail packet</DialogTitle>
        </DialogHeader>

        {!canUse ? (
          <div className="p-6 space-y-4">
            <div className="inline-flex items-center gap-2 border border-[#D5FF3F]/30 bg-[#D5FF3F]/5 rounded-full px-3 py-1.5">
              <Crown size={13} className="text-[#D5FF3F]" />
              <span className="text-xs font-mono uppercase tracking-widest text-[#D5FF3F]">
                Aura Basic + Plus
              </span>
            </div>
            <p className="text-[#A8ADA5] leading-relaxed">
              The cert-mail helper auto-fills the recipient bureau's address, formats your return address,
              generates a USPS Certified Mail cover sheet, and gives you a print-ready packet.
              Available on Basic and Plus.
            </p>
            <Link to="/app/pricing">
              <Button className="rounded-full btn-lime h-11 px-6">
                Upgrade from $39.99 <Sparkles size={14} className="ml-2" />
              </Button>
            </Link>
          </div>
        ) : !packet ? (
          <div className="space-y-4" data-testid="certmail-form">
            <p className="text-sm text-[#A8ADA5]">
              We'll auto-fill the bureau's dispute address. Your return address is used on the envelope
              and letter header — we remember it for next time.
            </p>
            <div className="grid md:grid-cols-2 gap-3">
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Your street address</Label>
                <Input value={addr.line1 || ""} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} placeholder="1234 Main St" data-testid="certmail-line1" className="mt-1 rounded-sm" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">Apt / Suite (optional)</Label>
                <Input value={addr.line2 || ""} onChange={(e) => setAddr({ ...addr, line2: e.target.value })} placeholder="Apt 5B" data-testid="certmail-line2" className="mt-1 rounded-sm" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">City</Label>
                <Input value={addr.city || ""} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="certmail-city" className="mt-1 rounded-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">State</Label>
                  <Input value={addr.state || ""} onChange={(e) => setAddr({ ...addr, state: e.target.value.toUpperCase().slice(0, 2) })} maxLength={2} placeholder="CA" data-testid="certmail-state" className="mt-1 rounded-sm" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-widest text-[#6D716C] font-mono">ZIP</Label>
                  <Input value={addr.zip || ""} onChange={(e) => setAddr({ ...addr, zip: e.target.value })} placeholder="90210" data-testid="certmail-zip" className="mt-1 rounded-sm" />
                </div>
              </div>
            </div>

            <div className="border-t border-[#26282C] pt-4">
              <button
                onClick={() => setOverrideOpen(!overrideOpen)}
                className="text-xs uppercase tracking-widest text-[#A8ADA5] font-mono hover:text-[#D5FF3F]"
                data-testid="certmail-override-toggle"
              >
                {overrideOpen ? "− Hide" : "+ Change recipient"} (default: bureau address)
              </button>
              {overrideOpen && (
                <div className="mt-3 space-y-2">
                  <div className="relative">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6D716C]" />
                    <Input
                      value={q}
                      onChange={(e) => setQ(e.target.value)}
                      placeholder="Search creditor (e.g. Midland, Capital One, Portfolio)"
                      data-testid="certmail-search"
                      className="rounded-sm pl-9"
                    />
                  </div>
                  {matches.length > 0 && (
                    <div className="border border-[#26282C] rounded-sm max-h-56 overflow-y-auto">
                      {matches.map((m) => (
                        <button
                          key={m.id}
                          onClick={() => {
                            setOverrideId(m.id);
                            setQ(m.name);
                            setMatches([]);
                          }}
                          data-testid={`certmail-match-${m.id}`}
                          className={`w-full text-left px-3 py-2 hover:bg-[#0B0C0E] flex items-center justify-between text-sm border-b border-[#26282C] last:border-0 ${
                            overrideId === m.id ? "bg-[#D5FF3F]/10 text-[#D5FF3F]" : "text-[#F5F3EE]"
                          }`}
                        >
                          <div>
                            <div>{m.name}</div>
                            <div className="text-[10px] text-[#6D716C] font-mono uppercase tracking-widest">
                              {m.category} · {m.address.city}, {m.address.state}
                            </div>
                          </div>
                          <span className="text-[10px] font-mono text-[#6D716C]">{Math.round(m.score * 100)}%</span>
                        </button>
                      ))}
                    </div>
                  )}
                  {overrideId && (
                    <div className="text-xs text-[#7CFF9B] font-mono">Overriding recipient with matched creditor.</div>
                  )}
                </div>
              )}
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={render}
                disabled={loading}
                data-testid="certmail-render-btn"
                className="rounded-full btn-lime h-11 px-6"
              >
                {loading ? "Building…" : (<><Mail size={15} className="mr-2" /> Build packet</>)}
              </Button>
            </div>
          </div>
        ) : (
          <>
            <div id="certmail-print-area" data-testid="certmail-packet" className="space-y-4">
              <h1>Certified Mail Packet — Aura Credit</h1>
              <div className="row" style={{ display: "flex", gap: 48, marginBottom: 24 }}>
                <div className="col" style={{ flex: 1 }}>
                  <div className="label" style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6D716C", marginBottom: 8 }}>From</div>
                  <pre className="whitespace-pre-wrap font-mono text-sm text-[#F5F3EE] leading-relaxed">
{packet.from_block}
                  </pre>
                </div>
                <div className="col" style={{ flex: 1 }}>
                  <div className="label" style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6D716C", marginBottom: 8 }}>To</div>
                  <pre className="whitespace-pre-wrap font-mono text-sm text-[#F5F3EE] leading-relaxed">
{packet.to_block}
                  </pre>
                  {packet.matched_creditor && (
                    <div className="text-[10px] uppercase tracking-widest text-[#7CFF9B] font-mono mt-2">
                      ✓ {packet.matched_creditor.name} · {packet.matched_creditor.category}
                    </div>
                  )}
                </div>
              </div>

              <hr />

              <div className="label" style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6D716C", marginBottom: 8 }}>Letter body</div>
              <pre className="whitespace-pre-wrap font-mono text-sm text-[#F5F3EE] leading-relaxed">
{packet.letter}
              </pre>

              <hr />

              <div className="label" style={{ fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6D716C", marginBottom: 8 }}>USPS certified-mail steps</div>
              <ol className="list-decimal pl-5 space-y-1 text-sm text-[#F5F3EE]">
                {packet.usps_steps.map((s, i) => (
                  <li key={`${i}-${(s || "step").slice(0, 40)}`}>{s}</li>
                ))}
              </ol>
            </div>
            <div className="flex justify-end gap-2 pt-4 border-t border-[#26282C]" data-testid="certmail-actions">
              <Button
                onClick={() => setPacket(null)}
                className="rounded-full btn-outline"
                data-testid="certmail-edit-btn"
              >
                Edit
              </Button>
              <Button
                onClick={print}
                className="rounded-full btn-lime"
                data-testid="certmail-print-btn"
              >
                <Printer size={15} className="mr-2" /> Print packet
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
